/**
 * shadcn's card and its parts; the title takes the theme's serif.
 *
 * Used by: the plant and bed cards (PlantCard, LocationCard).
 *
 * @module card
 */
import * as React from "react"

import { cn } from "@/lib/utils"

/** A bordered, raised box. */
const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "rounded-xl border bg-card text-card-foreground shadow",
      className
    )}
    {...props}
  />
))
Card.displayName = "Card"

/** What CardHeader takes besides a div's own attributes. */
interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The plant card's tight look (space-y-1 px-4 pb-2 pt-3) in place of the roomy default (space-y-1.5 p-6). */
  tight?: boolean
}

/** A card's top: title and description, padded; `tight` is the plant card's 16px look. */
const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, tight = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        tight ? "flex flex-col space-y-1 px-4 pb-2 pt-3" : "flex flex-col space-y-1.5 p-6",
        className
      )}
      {...props}
    />
  )
)
CardHeader.displayName = "CardHeader"

/** A card's title, in the theme's serif. */
const CardTitle = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("font-semibold leading-none tracking-tight font-display", className)}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

/** A muted line under a card's title. */
const CardDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

/** A card's body. */
const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />
))
CardContent.displayName = "CardContent"

/** A card's foot: a row of actions. */
const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
